/**
 * Certificate Layout Configuration
 * 证书布局配置
 *
 * This file contains the positioning configuration for different certificate types.
 * All positions are in pixels (px), based on image size: 1785px x 2526px
 *
 * 此文件包含不同证书类型的定位配置。
 * 所有位置以像素为单位，基于图片尺寸：1785px x 2526px
 */

export interface CertificateLayout {
  companyName: {
    top: number;
    left: number | string; // number for px, string like "50%" for percentage
    fontSize: number;
    fontWeight: number;
    width?: number | string;
    maxWidth?: number;
  };
  valuationResult: {
    top: number | string; // number for px, string like "50%" for percentage
    left: number | string;
    fontSize: number;
    fontWeight: number;
  };
  date: {
    bottom: number;
    left: number;
    fontSize: number;
  };
  certificateNumber: {
    bottom: number;
    left: number;
    fontSize: number;
  };
  signature: {
    bottom: number;
    left: number | string;
    fontSize: number;
    fontFamily: string;
  };
}

// General/Startup certificate layout (type 1, 2)
// Positions in px (Image: 1785px x 2526px)
export const generalLayout: CertificateLayout = {
  companyName: {
    top: 528, // px from top (based on original layout: margin-top: 528px)
    left: "50%", // center horizontally
    fontSize: 42, // px (2.6rem ≈ 42px)
    fontWeight: 700,
    width: "80%",
    maxWidth: 600, // px
  },
  valuationResult: {
    top: "50%", // center vertically
    left: "50%", // center horizontally
    fontSize: 36, // px (2.2rem ≈ 36px)
    fontWeight: 600,
  },
  date: {
    bottom: 364, // px from bottom (based on original: bottom: 36.4rem ≈ 364px)
    left: 80, // px from right
    fontSize: 24, // px (1.6rem ≈ 24px)
  },
  certificateNumber: {
    bottom: 295, // px from bottom (based on original: bottom: 29.5rem ≈ 295px)
    left: 80, // px from right
    fontSize: 24, // px (1.6rem ≈ 24px)
  },
  signature: {
    bottom: 176, // px from bottom (based on original: bottom: 17.6rem ≈ 176px)
    left: "50%", // center horizontally
    fontSize: 48, // px (3.3rem ≈ 48px)
    fontFamily: '"Dancing Script", cursive',
  },
};

// Patent certificate layout (type 8)
// Positions in px (Image: 1785px x 2526px)
export const patentLayout: CertificateLayout = {
  companyName: {
    top: 870,
    left: "50%",
    fontSize: 42,
    fontWeight: 700,
    width: "80%",
    maxWidth: 600,
  },
  valuationResult: {
    top: "52%",
    left: "50%",
    fontSize: 60,
    fontWeight: 600,
  },
  date: {
    bottom: 940,
    left: 1100,
    fontSize: 30,
  },
  certificateNumber: {
    bottom: 800,
    left: 1100,
    fontSize: 30,
  },
  signature: {
    bottom: 480,
    left: 1100,
    fontSize: 78,
    fontFamily: '"Dancing Script", cursive',
  },
};

// Bank certificate layout (type 9)
// Positions in px (Image: 1785px x 2526px)
export const bankLayout: CertificateLayout = {
  companyName: {
    top: 528,
    left: "50%",
    fontSize: 42,
    fontWeight: 700,
    width: "80%",
    maxWidth: 600,
  },
  valuationResult: {
    top: "50%",
    left: "50%",
    fontSize: 36,
    fontWeight: 600,
  },
  date: {
    bottom: 364,
    left: 80,
    fontSize: 24,
  },
  certificateNumber: {
    bottom: 295,
    left: 80,
    fontSize: 24,
  },
  signature: {
    bottom: 176,
    left: "50%",
    fontSize: 48,
    fontFamily: '"Dancing Script", cursive',
  },
};

/**
 * Get layout configuration based on company type
 * @param companyType 1=general, 2=innovation, 8=patent, 9=bank
 */
export function getCertificateLayout(companyType: number): CertificateLayout {
  switch (companyType) {
    case 8:
      return patentLayout;
    case 9:
      return bankLayout;
    case 1:
    case 2:
    default:
      return generalLayout;
  }
}
